import React, { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { Icon, Menu, MenuItem, ButtonBase } from "@mui/material";
import MDTypography from "components/MDTypography";

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
}));

const TopLabel = styled(MDTypography)(({ theme }) => ({
  lineHeight: "20px",
  letterSpacing: 0,
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.text.main || theme.palette.text.primary,
  marginBottom: 8,
}));

const Row = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  gap: 8,
}));

const Display = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flex: 1,
  border: 0,
  outline: 0,
  cursor: "pointer",
  borderRadius: 10,
  boxShadow: "0px 1px 4px 1px #00000040",
  background: "transparent",
  padding: "11px 10px 12px 10px",
  color: theme.palette.text.main || theme.palette.text.primary,
  textAlign: "left",
}));

const ArrowButton = styled(ButtonBase)(({ theme }) => ({
  flexShrink: 0,
  width: 35,
  height: 35,
  borderRadius: 10,
  boxShadow: "0px 1px 4px 1px #00000040",
  background: "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

function MDSelect({
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  fullWidth,
  menuMaxHeight,
  renderDisplay,
}) {
  const [open, setOpen] = useState(false);
  const displayRef = useRef(null);
  const anchorEl = displayRef.current;

  const normalized = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt) =>
      typeof opt === "string"
        ? { value: opt, label: opt }
        : { value: opt.value, label: opt.label }
    );
  }, [options]);

  const selectedLabel = useMemo(() => {
    const found = normalized.find((o) => o.value === value);
    return found ? found.label : placeholder || "Seleccionar";
  }, [normalized, value, placeholder]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSelect = (val) => {
    onChange?.(val);
    setOpen(false);
  };

  return (
    <Container
      className={className}
      style={{ width: fullWidth ? "100%" : undefined }}
    >
      {label ? <TopLabel>{label}</TopLabel> : null}
      <Row>
        <Display ref={displayRef} onClick={handleOpen} disabled={disabled}>
          {renderDisplay ? (
            renderDisplay(value, selectedLabel)
          ) : (
            <span style={{ fontSize: 12, fontWeight: 500 }}>
              {selectedLabel}
            </span>
          )}
        </Display>
        <ArrowButton onClick={handleOpen} disabled={disabled}>
          <Icon>{open ? "keyboard_arrow_up" : "keyboard_arrow_down"}</Icon>
        </ArrowButton>
      </Row>
      <Menu
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: (theme) => ({
            mt: 0.5,
            borderRadius: 2,
            boxShadow: "0px 4px 4px 0px #00000040",
            width: anchorEl ? anchorEl.offsetWidth : 221,
            maxHeight: menuMaxHeight || 220,
            backgroundColor: "transparent",
          }),
        }}
      >
        {normalized.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={opt.value === value}
            onClick={() => handleSelect(opt.value)}
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: (theme) =>
                theme.palette.text.main || theme.palette.text.primary,
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Container>
  );
}

MDSelect.defaultProps = {
  options: [],
  disabled: false,
  fullWidth: false,
  placeholder: "Seleccionar",
};

MDSelect.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ value: PropTypes.any, label: PropTypes.node }),
    ])
  ),
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.node,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  menuMaxHeight: PropTypes.number,
  renderDisplay: PropTypes.func,
};

export default MDSelect;
